import { SectionType, Translations } from 'src/types/publishedAssets';
import { decodeHtmlEntities } from './decodeHtmlEntities';
import { determineSectionId } from './determineSectionId';
import { isHeroSection } from './heroSection';

export type SectionTab = {
  id: string;
  slug: string;
  title: string;
  defaultTitle: string;
  isHero: boolean;
};

export type SectionTabs = {
  tabs: SectionTab[];
  ownerSlugBySectionId: Record<string, string | null>;
};

type TranslateFn = (translations: Translations | undefined, key: string) => string;

// A leading run of untitled sections has no preceding titled tab to merge into; without this,
// they'd have a null owner and render under every tab instead of moulding into the first tab.
function buildNextTitledSlugBySectionId(sections: SectionType[], translateDefault: TranslateFn): (string | null)[] {
  const nextTitledSlugBySectionId: (string | null)[] = Array.from({ length: sections.length }, () => null);
  let followingTitledSlug: string | null = null;

  for (let index = sections.length - 1; index >= 0; index -= 1) {
    const section = sections[index];
    const defaultTitle = translateDefault(section.translations, 'label');
    nextTitledSlugBySectionId[index] = followingTitledSlug;
    if (defaultTitle && !isHeroSection(section)) {
      followingTitledSlug = determineSectionId(defaultTitle);
    }
  }

  return nextTitledSlugBySectionId;
}

export function getSectionTabs(
  sections: SectionType[],
  translate: TranslateFn,
  translateDefault: TranslateFn,
): SectionTabs {
  const tabs: SectionTab[] = [];
  const ownerSlugBySectionId: Record<string, string | null> = {};
  const nextTitledSlugBySectionId = buildNextTitledSlugBySectionId(sections, translateDefault);
  let precedingTitledSlug: string | null = null;

  sections.forEach((section, index) => {
    const defaultTitle = translateDefault(section.translations, 'label');
    const title = translate(section.translations, 'label');
    const isHero = isHeroSection(section);

    if (defaultTitle) {
      // The slug is derived from the stored value so existing anchors keep working; only
      // the caption is entity-decoded. `translateDefault` is intentionally not decoded for
      // that reason, which leaves this fallback (a section titled only in a non-default
      // locale) as the one display path that would otherwise still show `&amp;`.
      const slug = determineSectionId(defaultTitle);
      tabs.push({ id: section.id, slug, title: title || decodeHtmlEntities(defaultTitle), defaultTitle, isHero });
      ownerSlugBySectionId[section.id] = slug;
      if (!isHero) {
        precedingTitledSlug = slug;
      }
    } else {
      ownerSlugBySectionId[section.id] = precedingTitledSlug ?? nextTitledSlugBySectionId[index];
    }
  });

  return { tabs, ownerSlugBySectionId };
}
